/* Only definitive PDP bundles use this contract. Ordinary cart lines are untouched. */
(() => {
  'use strict';
  if (window.GndBundleCart) return;
  const source = 'definitive-pdp';
  const gifts = Object.freeze({
    guarantee: 63446695837836,
    shipping: 63446695903372,
    mystery: 63446696558732,
    draw: 63446702030988
  });
  const originalGifts = {gift_1:'guarantee',gift_2:'shipping',gift_3:'mystery',gift_4:'draw'};
  const root = () => window.Shopify?.routes?.root || '/';
  const members = (cart, group) => (cart.items || []).filter(item => item.properties?._gnd_source === source && item.properties._gnd_group === group);
  const errorMessage = payload => typeof payload?.description === 'string' ? payload.description : typeof payload?.message === 'string' ? payload.message : 'The complete bundle could not be added. Please try again.';
  async function request(path, body) {
    const response = await fetch(root() + path, {
      method: body ? 'POST' : 'GET', credentials:'same-origin', cache:'no-store',
      headers:{Accept:'application/json',...(body ? {'Content-Type':'application/json','X-GND-Bundle':'1'} : {})},
      ...(body ? {body:JSON.stringify(body)} : {})
    });
    const payload = await response.json();
    if (!response.ok || payload.status || payload.errors) throw new Error(errorMessage(payload));
    return payload;
  }
  function planFor(product) {
    if (!product?.id || !product.available) throw new Error('This bundle is not available yet.');
    let plans = (product.plans || []).filter(plan => plan.recurring && Number.isSafeInteger(Number(plan.id)) && Number(plan.id) > 0);
    const skioPlans = plans.filter(plan => String(plan.appId || '').toUpperCase() === 'SKIO');
    if (skioPlans.length) plans = skioPlans;
    // Allocations belong to the selected product variant. Never reuse another bundle's plan.
    const monthly = plans.filter(plan => [plan.name, ...(plan.options || []).map(option => option.value)].some(value =>
      /\b(?:monthly|(?:every\s+)?(?:1\s+months?|30\s+days?)|every\s+month)\b/i.test(String(value || '').replace(/[-_]/g,' '))
    ));
    const selected = monthly.length === 1 ? monthly[0] : plans.length === 1 ? plans[0] : null;
    if (!selected) throw new Error('The subscription for this bundle is not available yet.');
    return selected;
  }
  async function refreshProduct(product) {
    if (!product?.handle) throw new Error('This bundle is not available yet. Please refresh the page.');
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    let data;
    try {
      const response = await fetch(root()+'products/'+encodeURIComponent(product.handle)+'.js', {
        credentials:'same-origin', cache:'no-store', headers:{Accept:'application/json'}, signal:controller.signal
      });
      if (!response.ok) throw new Error('This bundle is temporarily unavailable. Please try another option.');
      data = await response.json();
    } catch (error) {
      if (error.name === 'AbortError' || error instanceof TypeError) throw new Error('Could not check this bundle. Please check your connection and try again.');
      throw error;
    } finally { clearTimeout(timeout); }
    const variant = product.overrideId
      ? data.variants?.find(item => Number(item.id) === Number(product.overrideId))
      : data.variants?.find(item => item.available) || data.variants?.[0];
    if (!variant) throw new Error('This bundle is temporarily unavailable. Please try another option.');
    const plans = (variant.selling_plan_allocations || []).flatMap(allocation => {
      const group = (data.selling_plan_groups || []).find(item => item.id === allocation.selling_plan_group_id);
      const plan = group?.selling_plans?.find(item => Number(item.id) === Number(allocation.selling_plan_id));
      return plan ? [{id:plan.id,name:plan.name,appId:group.app_id,recurring:plan.recurring_deliveries,options:plan.options,price:allocation.price,prices:allocation.price_adjustments,adjustments:plan.price_adjustments}] : [];
    });
    return {...product,id:variant.id,available:variant.available,type:data.type,plans};
  }
  function buildItems(products, bundle, group, eligibleGifts) {
    if (!['starter','duo','complete'].includes(bundle.key)) throw new Error('Please select a bundle.');
    const product = products[bundle.key];
    const plan = planFor(product);
    const productType = String(product.type || '').trim().toLowerCase();
    if (productType === 'dynamic box') throw new Error('This bundle is not available yet.');
    const props = role => ({_gnd_source:source,_gnd_group:group,_gnd_bundle:bundle.key,_gnd_title:bundle.title,_gnd_role:role});
    // Skio Preset boxes need only their parent variant; Skio adds their contents after checkout.
    // Keep our gift-group metadata separate from Skio's internal box selection properties.
    const properties = props('bundle');
    if (productType === 'classic box') properties._isClassicBoxV2 = true;
    if (bundle.contents) properties._gnd_contents = String(bundle.contents).slice(0,240);
    if (bundle.images?.length) properties._gnd_images = JSON.stringify(bundle.images.slice(0,3));
    const items = [{id:Number(product.id),quantity:1,selling_plan:Number(plan.id),properties}];
    const seen = new Set();
    for (const gift of eligibleGifts) {
      const kind = gift.kind === 'auto' ? originalGifts[gift.key] : gift.kind;
      if (!gifts[kind]) throw new Error('A gift is not configured for this bundle.');
      if (seen.has(kind)) continue;
      seen.add(kind);
      items.push({id:gifts[kind],quantity:1,properties:props('gift')});
    }
    return items;
  }
  function complete(items, expected) {
    return items.length === expected.length && expected.every(wanted => items.some(item =>
      Number(item.variant_id || item.id) === wanted.id && item.quantity === wanted.quantity &&
      item.properties?._gnd_role === wanted.properties._gnd_role &&
      (!wanted.properties._isClassicBoxV2 || String(item.properties._isClassicBoxV2) === 'true') &&
      Number(item.selling_plan_allocation?.selling_plan?.id || 0) === Number(wanted.selling_plan || 0) &&
      (wanted.properties._gnd_role !== 'gift' || item.final_line_price === 0)
    ));
  }
  async function remove(group, cart) {
    cart = cart || await request('cart.js');
    const lines = members(cart, group);
    if (!lines.length) return cart;
    const updates = Object.fromEntries(lines.map(item => [item.key,0]));
    const updated = await request('cart/update.js', {updates,sections:sections(),sections_url:location.pathname});
    if (members(updated,group).length) throw new Error('The bundle could not be removed. Please try again.');
    return updated;
  }
  function sections() {
    return [...new Set([...(document.querySelector('cart-drawer') ? ['cart-drawer'] : []),'cart-icon-bubble',...['main-cart-items','main-cart-footer'].map(id => document.getElementById(id)?.dataset.id).filter(Boolean)])].slice(0,5);
  }
  async function render(payload, open, trigger) {
    const api = window.NuoraNativeCartDrawer;
    if (window.GndUpcart) {
      await window.GndUpcart.render(open);
    } else if (api) {
      if (!api.render(payload.sections, {open,trigger})) await (open ? api.refreshAndOpen(trigger) : api.refresh());
    } else {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      if (open) location.assign(root()+'cart');
    }
    for (const id of ['main-cart-items','main-cart-footer']) {
      const target = document.getElementById(id);
      const markup = target && payload.sections?.[target.dataset.id];
      if (!markup) continue;
      const parsed = new DOMParser().parseFromString(markup,'text/html');
      const replacement = parsed.getElementById(id);
      if (replacement) target.replaceWith(replacement);
    }
    if (typeof payload.item_count === 'number') document.querySelectorAll('cart-items,#main-cart-footer,cart-drawer,cart-drawer-items').forEach(el=>el.classList.toggle('is-empty',payload.item_count===0));
    document.dispatchEvent(new CustomEvent('gnd:cart-updated'));
  }
  async function add(items, group, trigger) {
    let payload;
    try {
      payload = await request('cart/add.js',{items,sections:sections(),sections_url:location.pathname});
      // Read the real cart before opening it; never leave an incomplete or charged gift set.
      const cart = await request('cart.js');
      if (!complete(members(cart,group),items)) throw new Error('The complete bundle is not available.');
    } catch (error) {
      let confirmed;
      try {
        const cart = await request('cart.js');
        if (complete(members(cart,group),items)) {
          confirmed = cart;
        } else {
          const cleaned = await remove(group,cart);
          await render(cleaned,false);
        }
      } catch (_) {
        throw new Error('Please check your cart before trying again. The bundle could not be confirmed.');
      }
      if (confirmed) {
        // A lost add response is not a failed purchase if the entire group is in the cart.
        try { await render(confirmed,true,trigger); }
        catch (_) { location.assign(root()+'cart'); }
        return;
      }
      throw error;
    }
    // A drawer rendering failure must not turn a successful add into another add attempt.
    try { await render(payload,true,trigger); }
    catch (_) { location.assign(root()+'cart'); }
  }
  let adding = false;
  async function addWithCartLock(...args) {
    if (adding) throw new Error('Your bundle is already being added. Please wait.');
    adding = true;
    window.GndUpcart?.setBusy(true);
    try { return await add(...args); }
    finally { adding = false; window.GndUpcart?.setBusy(false); }
  }
  window.GndBundleCart = {buildItems,planFor,refreshProduct,add:addWithCartLock,remove,members,complete,gifts};
  let removing = false;
  document.addEventListener('click',async event => {
    const button = event.target.closest('[data-gnd-remove-group]');
    if (!button) return;
    event.preventDefault();
    if (removing) return;
    removing = true;
    const card = button.closest('[data-gnd-cart-group]');
    const status = card.querySelector('[data-gnd-group-status]');
    const drawer = button.closest('cart-drawer');
    const checkout = document.querySelectorAll('[name="checkout"]');
    button.disabled = true; card.setAttribute('aria-busy','true'); status.textContent = '';
    checkout.forEach(el=>{el.dataset.gndWasDisabled=String(el.disabled);el.disabled=true;});
    try {
      const updated = await remove(button.dataset.gndRemoveGroup);
      await render(updated,Boolean(drawer?.classList.contains('active')));
      (drawer?.querySelector('.drawer__close') || document.querySelector('cart-items h1'))?.focus({preventScroll:true});
    } catch (error) {
      status.textContent = error.message || 'Could not remove this bundle. Please try again.';
    } finally {
      removing = false; button.disabled = false;card.removeAttribute('aria-busy');
      document.querySelectorAll('[data-gnd-was-disabled]').forEach(el=>{el.disabled=el.dataset.gndWasDisabled==='true';delete el.dataset.gndWasDisabled;});
    }
  });
})();
